import { useQuery } from '@tanstack/react-query';
import { IntegrationsApi, Permission } from '@up2stay/up2promo-api-client';

import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useLocations } from '@/core/hooks/use-locations';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useChannelLocations } from '@/core/hooks/use-reputation';
import {
  countNeedsActionForProvider,
  GOOGLE_REVIEW_PROVIDER,
  isProviderConnectionSetupComplete,
} from '@/core/lib/review-channel-setup';
import { integrationsQueryKeys } from '@/core/query-keys/integrations-query-keys';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

export type ReputationConnectionSetupBannerVariant =
  | 'connect_google'
  | 'reconnect_google'
  | 'link_locations';

function hasGoogleChannelMappings(
  channelLocations: { provider: string; providerLocationId?: string | null }[],
): boolean {
  return channelLocations.some(
    (c) =>
      c.provider === GOOGLE_REVIEW_PROVIDER &&
      Boolean(c.providerLocationId?.trim()),
  );
}

export function useReputationConnectionSetupState() {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();
  const { can, isLoading: permissionsLoading } = usePermissions();
  const canReadReputation = can(Permission.REPUTATION_READ);
  const canReadIntegrations = can(Permission.INTEGRATIONS_READ);
  // Free (and locked) workspaces must not hit Integrations — the global
  // upgrade interceptor would open "not in your plan" on every home load.
  // Members also must not hit admin-gated reputation/integrations routes (403).
  const reputationEnabled =
    !entitlementsLoading &&
    !permissionsLoading &&
    isFeatureEnabled('feature.reputation') &&
    canReadReputation;
  const integrationsEnabled =
    reputationEnabled &&
    isFeatureEnabled('feature.integrations') &&
    canReadIntegrations;
  const setupQueriesEnabled = Boolean(workspaceId) && integrationsEnabled;

  const { data: integrationsData, isPending: integrationsPending } = useQuery({
    queryKey: integrationsQueryKeys.all(workspaceId),
    queryFn: async () => {
      if (!workspaceId) return null;
      const response = await IntegrationsApi.list({
        path: { workspaceId },
      });
      return response.data ?? null;
    },
    enabled: setupQueriesEnabled,
    staleTime: 60_000,
  });

  const googleConnected =
    integrationsData?.integrations?.some(
      (i) => i.provider === GOOGLE_REVIEW_PROVIDER && i.connected,
    ) ?? false;

  const { locations, isPending: locationsPending } = useLocations(workspaceId, {
    limit: 100,
    enabled: setupQueriesEnabled,
  });

  const { data: channelLocations = [], isPending: channelsPending } =
    useChannelLocations(setupQueriesEnabled ? workspaceId : undefined);

  const isReady =
    !!workspaceId &&
    !entitlementsLoading &&
    !permissionsLoading &&
    (!integrationsEnabled ||
      (!integrationsPending && !locationsPending && !channelsPending));

  const locationsNeedLinking =
    googleConnected &&
    locations.length > 0 &&
    !isProviderConnectionSetupComplete(
      locations,
      channelLocations,
      GOOGLE_REVIEW_PROVIDER,
    );

  const hadGoogleChannelMappings = hasGoogleChannelMappings(channelLocations);

  const needsReconnectGoogle =
    !googleConnected &&
    integrationsData != null &&
    locations.length > 0 &&
    hadGoogleChannelMappings;

  const needsConnectGoogle =
    !googleConnected &&
    integrationsData != null &&
    locations.length > 0 &&
    !hadGoogleChannelMappings;

  const needsSetup =
    locationsNeedLinking || needsReconnectGoogle || needsConnectGoogle;

  const needsActionCount = locationsNeedLinking
    ? countNeedsActionForProvider(
        locations,
        channelLocations,
        GOOGLE_REVIEW_PROVIDER,
      )
    : locations.length;

  const bannerVariant: ReputationConnectionSetupBannerVariant | null =
    needsReconnectGoogle
      ? 'reconnect_google'
      : needsConnectGoogle
        ? 'connect_google'
        : locationsNeedLinking
          ? 'link_locations'
          : null;

  const showConnectionBanner = isReady && needsSetup;

  return {
    isReady,
    googleConnected,
    needsSetup,
    needsActionCount,
    bannerVariant,
    showConnectionBanner,
    suppressSyncBanner: showConnectionBanner,
  };
}
